// information on the error handling structure can be found here: https://calix.atlassian.net/wiki/spaces/ECOM/pages/5161255324/Quote+Manager+Clone+Route
import { Container } from '@Calix-Commerce/design-system';
import {
  loadingState,
  currentQuoteValidationState,
  selectedAccountState,
  authenticationState,
  currentQuoteState,
  loggedInAccountState,
} from 'state/atoms';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { useEffect, useState } from 'react';
import { useAppNavigation } from 'utils/hooks';
import { useQuoteActionController } from 'utils/hooks';
import { storageCurrentQuoteSelector } from 'state/selectors';
import { ErrorMessage } from 'types/Error';
import { UnsavedChangesModal } from 'components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { VAR } from 'connectors/userTypes';
import { SingleActionModal } from 'components/modals/SingleActionModal';
import { FETCH_POLICY } from 'utils/constants/general';
import { defaultCurrentQuoteValidationState } from 'state/defaults';
import { calculateQuoteValidationState } from 'utils/helpers/quoteValidation';
import { CreatedBySystem } from 'types/Quote';

const QuoteClone = () => {
  const { transactionId } = useParams();
  const selectedAccount = useRecoilValue(selectedAccountState);
  const { accountId } = selectedAccount;
  const [currentQuote, setCurrentQuote] = useRecoilState(storageCurrentQuoteSelector);
  const isNewQuote = Boolean(currentQuote.transactionId) === false;
  const validationState = useRecoilValue(currentQuoteValidationState);
  const authenticationData = useRecoilValue(authenticationState);
  const { userType } = authenticationData;
  const loggedInAccount = useRecoilValue(loggedInAccountState);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showAccountRedirectModal, setShowAccountRedirectModal] = useState(false);
  const [showQuoteDetailsRedirectModal, setShowQuoteDetailsRedirectModal] = useState(false);
  const [showQuoteListRedirectModal, setShowQuoteListRedirectModal] = useState(false);
  const { redirectToQuoteDetails, redirectToAccountSearch, redirectToQuoteList } =
    useAppNavigation();
  const setGlobalLoader = useSetRecoilState(loadingState);
  const { loadQuote, alterQuote, createQuote, cloneQuote } = useQuoteActionController();

  const onSaveUnsavedChanges = () => {
    setGlobalLoader(true);
    setShowUnsavedModal(false);

    if (isNewQuote) {
      createQuote({
        quoteData: { ...currentQuote, accountId },
        validationData: validationState,
        redirectToNewQuote: false,
      })
        .then(() => {
          startCloneProcess();
        })
        .catch((error) => {
          finalModalErrorHandling(error);
        });
    } else {
      alterQuote({ quoteData: currentQuote, validationData: validationState })
        .then(() => {
          startCloneProcess();
        })
        .catch((error) => {
          finalModalErrorHandling(error);
        });
    }
  };

  const discardUnsavedChangesModal = () => {
    setShowUnsavedModal(false);
    setGlobalLoader(true);
    startCloneProcess();
  };

  const cancelUnsavedChangesModal = () => {
    setShowUnsavedModal(false);
    if (transactionId) {
      redirectToQuoteList({
        replaceCurrentLocation: true,
      });
    } else {
      redirectToQuoteList({ replaceCurrentLocation: false });
    }
  };

  const initialErrorHandling = (error) => {
    try {
      setGlobalLoader(false);

      // if there is an unsaved, non-var quote, allow the user clone options. Otherwise, exit without cloning
      if (error.message === ErrorMessage.MODIFIED_QUOTE && userType !== VAR) {
        setShowUnsavedModal(true);
      } else {
        finalModalErrorHandling(undefined);
      }
    } catch (error) {
      finalModalErrorHandling(error);
    }
  };

  const finalModalErrorHandling = (error: unknown | undefined) => {
    setGlobalLoader(false);

    if (error) {
      console.error('Unhandlable error encountered, redirecting away from clone page: ', error);
    }

    if (userType === VAR) {
      setShowAccountRedirectModal(true);
    } else if (currentQuoteState) {
      setShowQuoteDetailsRedirectModal(true);
    } else {
      setShowQuoteListRedirectModal(true);
    }
  };

  const startCloneProcess = async () => {
    try {
      const targetQuote = await loadQuote({
        transactionId: transactionId,
        overwriteLocalQuote: true,
        fetchPolicy: FETCH_POLICY.NO_CACHE,
        skipLockQuote: true,
        setCurrentState: false,
      });

      const targetQuoteValidation = calculateQuoteValidationState({
        quoteData: targetQuote,
        //set validation to default since we're running validation checks for the first time on the loaded quote
        quoteValidation: { ...defaultCurrentQuoteValidationState },
        account: selectedAccount,
        authenticationInformation: authenticationData,
        loggedInAccount: loggedInAccount,
      });

      const clonedQuote = await cloneQuote({
        quoteData: targetQuote,
        validationData: targetQuoteValidation,
        redirectToNewQuote: false,
        createdBySystem: CreatedBySystem.EquipmentOrderDocument,
      });

      setCurrentQuote({
        ...clonedQuote,
        shippingAddress: {
          ...clonedQuote.shippingAddress,
          country: targetQuote.shippingAddress.country,
        },
      });
      redirectToQuoteDetails({
        overrideTransactionId: clonedQuote.transactionId,
        replaceCurrentLocation: true,
      });
    } catch (error) {
      initialErrorHandling(error);
    }
  };

  useEffect(() => {
    const hasExistingUnsavedChanges = validationState.isModified;

    if (hasExistingUnsavedChanges) {
      setShowUnsavedModal(true);
      return;
    }

    setGlobalLoader(true);
    startCloneProcess();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onCancel={cancelUnsavedChangesModal}
        onConfirmation={onSaveUnsavedChanges}
        onDiscard={discardUnsavedChangesModal}
        modalContent={
          <p>
            You're leaving{' '}
            {currentQuote.transactionId ? (
              <span>
                quote <b>{`${currentQuote.transactionId}`}</b> with unsaved changes
              </span>
            ) : (
              <span>{'an unsaved quote'}</span>
            )}{' '}
            to clone <b>{transactionId as string}</b>
          </p>
        }
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showQuoteListRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click OK to navigate back to the quote list page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() => redirectToQuoteList({ replaceCurrentLocation: true })}
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showQuoteDetailsRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click OK to navigate back to the quote details page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() =>
          redirectToQuoteDetails({
            overrideTransactionId: transactionId,
            replaceCurrentLocation: true,
          })
        }
      />

      <SingleActionModal
        title={'Error Cloning'}
        isOpen={showAccountRedirectModal}
        content={
          'An error was encountered while attempting to clone your quote. Click OK to navigate back to the account search page.'
        }
        confirmButtonText={'OK'}
        onConfirmation={() => redirectToAccountSearch({ replaceCurrentLocation: true })}
      />
    </Container>
  );
};

export { QuoteClone };
